/**
 * États chargés de la page exercice : exercice, leçon parente, formulaire,
 * chargement et erreur ; chargement depuis l'API au montage.
 */

import { useState, useCallback, useEffect } from 'react';
import { BookExercise, BookLesson } from '../../../types';
import BookContentApiService from '../../../services/bookContentApi';
import { AppEnvironment } from '../../../config/environment';
import { logger } from '../../../lib/logger';
import { toApiError, toUserMessage } from '../../../lib/http';
import { ExerciseFormData } from './types';

const HTTP_INTROUVABLE = 404;

/** Valeurs initiales du formulaire (création). */
const initialFormData = (lessonIdFromParams: string | null): ExerciseFormData => ({
  lessonId: lessonIdFromParams || '',
  exerciseNumber: 1,
  title: '',
  statement: '',
  correction: '',
  illustrationUrl: '',
  illustrationLatex: '',
  difficulty: 'medium',
  points: 10,
  hints: [],
  keywords: [],
  status: 'draft',
  url: '',
  resume: ''
});

/**
 * Message de la fiche quand l'exercice ne se lit pas. 404 : l'exercice n'existe
 * pas dans l'environnement affiché (supprimé, ou signalé ailleurs) — recette
 * préprod du 22/10, fiche ouverte depuis la file de modération.
 */
export const messageErreurExercice = (err: unknown): string =>
  toApiError(err, '').status === HTTP_INTROUVABLE
    ? "Exercice introuvable dans cet environnement (supprimé ou signalé depuis un autre environnement)"
    : toUserMessage(err, "Erreur lors du chargement de l'exercice");

/**
 * Leçon parente, pour son titre seulement : une leçon absente ne doit pas
 * masquer l'exercice, déjà chargé (null + journal).
 */
const lireLeconParente = async (env: AppEnvironment, lessonId: string): Promise<BookLesson | null> => {
  try {
    return await BookContentApiService.getBookLessonById(env, lessonId);
  } catch (err) {
    logger.warn('[useExerciseLoader][lireLeconParente] leçon parente illisible', { lessonId, env, err });
    return null;
  }
};

/** Porte les états chargés de l'exercice et déclenche le chargement (exercice ou leçon parente). */
export const useExerciseLoader = (
  id: string | undefined,
  env: AppEnvironment,
  isNewMode: boolean,
  lessonIdFromParams: string | null
) => {
  const [exercise, setExercise] = useState<BookExercise | null>(null);
  const [lesson, setLesson] = useState<BookLesson | null>(null);
  const [loading, setLoading] = useState(!isNewMode);
  const [error, setError] = useState<string>('');
  // Données du formulaire
  const [formData, setFormData] = useState<ExerciseFormData>(() => initialFormData(lessonIdFromParams));

  // Charger l'exercice existant
  const loadExercise = useCallback(async () => {
    if (isNewMode) return;

    try {
      setLoading(true);
      const data = await BookContentApiService.getBookExerciseById(env, id!);
      setExercise(data);
      setFormData(data);
      if (data.lessonId) {
        setLesson(await lireLeconParente(env, data.lessonId));
      }
    } catch (err: unknown) {
      logger.error('[useExerciseLoader][loadExercise] exercice illisible', { id, env, err });
      setError(messageErreurExercice(err));
    } finally {
      setLoading(false);
    }
  }, [id, env, isNewMode]);

  // Charger la leçon parente (pour création)
  const loadParentLesson = useCallback(async () => {
    if (!isNewMode || !lessonIdFromParams) return;

    try {
      const lessonData = await BookContentApiService.getBookLessonById(env, lessonIdFromParams);
      setLesson(lessonData);
    } catch (err) {
      logger.error('Erreur lors du chargement de la leçon parente:', err);
    }
  }, [lessonIdFromParams, env, isNewMode]);

  useEffect(() => {
    if (isNewMode) {
      loadParentLesson();
    } else if (id) {
      loadExercise();
    }
  }, [loadExercise, loadParentLesson, id, isNewMode]);

  return { exercise, setExercise, lesson, loading, error, setError, formData, setFormData };
};
